import { 
  collection, 
  getDocs, 
  query, 
  where, 
  updateDoc, 
  doc,
  setDoc 
} from 'firebase/firestore';
import { db } from '../firebase';
import TransactionLogger from './transactionLogger';

export class UserManagement {
  /**
   * Find a user by email address
   * @param {string} email - The email address to search for
   * @returns {Object|null} - User object if found, null otherwise
   */
  static async findUserByEmail(email) {
    try {
      const q = query(collection(db, 'users'), where('email', '==', email));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return null;
      }
      
      const userDoc = snapshot.docs[0];
      return {
        id: userDoc.id,
        ...userDoc.data()
      };
    } catch (error) {
      console.error('Error finding user by email:', error);
      throw error;
    }
  }

  /**
   * Update a user's role
   * @param {string} userId - The user's document ID
   * @param {string} newRole - The new role ('admin' or 'organizer')
   * @param {string} adminUserId - The admin user ID making the change
   * @param {string} adminEmail - The admin user email making the change
   * @returns {Object} - Result object with success status
   */
  static async updateUserRole(userId, newRole, adminUserId, adminEmail) {
    try {
      const userRef = doc(db, 'users', userId);
      
      // Get current user data for logging
      const currentUser = await this.findUserByEmail(adminEmail);
      if (!currentUser) {
        throw new Error('Admin user not found');
      }

      // Update the user's role
      await updateDoc(userRef, {
        role: newRole,
        updatedAt: new Date(),
        updatedBy: adminUserId
      });

      // Log the transaction
      await TransactionLogger.logTransaction(
        'UPDATE',
        'USER',
        userId,
        { role: 'previous_role' }, // We don't have the old role easily accessible
        { role: newRole },
        adminUserId,
        adminEmail
      );

      console.log(`User role updated to ${newRole} successfully`);
      return { success: true, message: `User role updated to ${newRole}` };
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  }

  /**
   * Make a user an administrator by email
   * @param {string} email - The email address of the user to make admin
   * @param {string} adminUserId - The admin user ID making the change
   * @param {string} adminEmail - The admin user email making the change
   * @returns {Object} - Result object with success status
   */
  static async makeUserAdmin(email, adminUserId, adminEmail) {
    try {
      // Find the user by email
      const user = await this.findUserByEmail(email);
      
      if (!user) {
        throw new Error(`User with email ${email} not found`);
      }

      // Update their role to admin
      await this.updateUserRole(user.id, 'admin', adminUserId, adminEmail);

      return { 
        success: true, 
        message: `User ${email} has been made an administrator`,
        user: {
          id: user.id,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          role: 'admin'
        }
      };
    } catch (error) {
      console.error('Error making user admin:', error);
      throw error;
    }
  }

  /**
   * Create a basic admin user if they don't exist
   * @param {string} email - The email address
   * @param {string} firstName - First name
   * @param {string} lastName - Last name
   * @param {string} organizationName - Organization name
   * @param {string} adminUserId - The admin user ID creating the user
   * @param {string} adminEmail - The admin user email creating the user
   * @returns {Object} - Result object with success status
   */
  static async createAdminUser(email, firstName, lastName, organizationName, adminUserId, adminEmail) {
    try {
      // Check if user already exists
      const existingUser = await this.findUserByEmail(email);
      if (existingUser) {
        throw new Error(`User with email ${email} already exists`);
      }

      // Create a new user document
      const userData = {
        email,
        firstName,
        lastName,
        organizationName,
        role: 'admin',
        createdAt: new Date(),
        createdBy: adminUserId
      };

      // Note: This creates a Firestore document but doesn't create Firebase Auth user
      // The user would need to sign up through the normal signup process
      const userRef = doc(collection(db, 'users'));
      await setDoc(userRef, userData);

      // Log the transaction
      await TransactionLogger.logTransaction(
        'CREATE',
        'USER',
        userRef.id,
        null,
        userData,
        adminUserId,
        adminEmail
      );

      console.log(`Admin user created: ${email}`);
      return { 
        success: true, 
        message: `Admin user ${email} created successfully`,
        userId: userRef.id
      };
    } catch (error) {
      console.error('Error creating admin user:', error);
      throw error;
    }
  }

  /**
   * Get all users
   * @returns {Array} - Array of user objects
   */
  static async getAllUsers() {
    try {
      const snapshot = await getDocs(collection(db, 'users'));
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }
}

export default UserManagement;
